import React from 'react';
import { ParsedTrackedVehicle } from '@/api/requests/oram/utils/oramDataParser';
import { useAppConstantsContext } from '@/context/AppConstants';
import { Automobile } from '@/reports/types/vehicles';
import { Button, Grid, Icons, Text, useTheme } from '@/theme/index';
import { ScheduleServiceButton } from './ScheduleServiceButton/ScheduleServiceButton';
import { commaFormat } from '../../utils';
import {
  StaticServiceCardChildren,
  InProgressServiceCard,
  InitialServiceCard,
  MyGarageServiceCard,
} from '../MyGarageServiceCard/MyGarageServiceCard';
import { AlertTag } from '../components';
import { RegistrationParams, ServiceItemParams } from '../oramInterfaces';

interface MyGarageServiceCardContainerProps {
  vehicle?: Automobile;
  oramData?: ParsedTrackedVehicle;
  handleViewReport: (sectionId?: string) => void;
  handleCreateServiceItem: (serviceItem: ServiceItemParams) => void;
  handleServiceItemComplete: (
    serviceItemId: number,
    lastChangeMileage: string
  ) => void;
  handleCreateRegistration: (registration: RegistrationParams) => void;
}

interface AlertTextDisplayProps {
  alertText: string | undefined;
  bgColor: string | undefined;
}

const AlertTextDisplay = ({ alertText, bgColor }: AlertTextDisplayProps) => {
  const theme = useTheme();
  if (!alertText || !bgColor) return null;

  const textArray = alertText?.split(' ');
  const needsIcon = textArray?.includes('needed');

  type AlertType = 'active' | 'error';
  const obj = {
    active: theme.palette.action.active,
    error: theme.palette.error.main,
  }[bgColor as AlertType];

  return (
    <AlertTag sx={{ backgroundColor: obj }}>
      <Text display="flex" color="primary.contrastText">
        {needsIcon && <Icons.Warning fontSize="small" sx={{ mr: 0.5 }} />}
        {alertText}
      </Text>
    </AlertTag>
  );
};

export const MyGarageServiceCardContainer: React.FC<
  MyGarageServiceCardContainerProps
> = ({
  vehicle,
  oramData,
  handleViewReport,
  handleCreateServiceItem,
  handleServiceItemComplete,
  handleCreateRegistration,
}) => {
  const {
    images: { imgMyGarage },
  } = useAppConstantsContext();
  const ownershipCost = vehicle?.ownership_costs?.costs[0]?.total
    ? commaFormat(vehicle.ownership_costs.costs[0].total)
    : 'N/A';

  const activeWarranties = getActiveWarranties(vehicle);
  function getActiveWarranties(vehicle: Automobile | undefined) {
    const warranties = vehicle?.warranties;
    if (!warranties || warranties.length <= 0) return 'No active warranties';
    const activeWarranties = warranties.filter(
      (warranty) => warranty.is_active
    ).length;
    if (!activeWarranties || activeWarranties <= 0) {
      return 'No active warranties';
    }

    return `${activeWarranties} active ${
      activeWarranties > 1 ? 'warranties' : 'warranty'
    }`;
  }

  const oilChange = oramData?.serviceItems.find(
    (item) => item.serviceType === 'oil change'
  );
  const registration = oramData?.registration;

  const tireChange = oramData?.serviceItems.find(
    (item) => item.serviceType === 'tire change'
  );
  const brakeChange = oramData?.serviceItems.find(
    (item) => item.serviceType === 'brake change'
  );

  const serviceCards = [
    {
      icon: imgMyGarage?.imgMyGarageSellYourCar,
      primaryText: 'Estimated yearly cost',
      children: (
        <StaticServiceCardChildren
          text={ownershipCost}
          handleClick={() => handleViewReport('vehicle-ownership-cost')}
        />
      ),
      height: '180px',
    },
    {
      icon: imgMyGarage?.imgMyGarageStar,
      primaryText: 'Warranties',
      children: (
        <StaticServiceCardChildren
          text={activeWarranties}
          handleClick={() => handleViewReport('vehicle-warranty')}
        />
      ),
      height: '180px',
    },
    {
      icon: imgMyGarage?.imgMyGarageTitleCheck,
      primaryText: 'Registration',
      alertTag: (
        <AlertTextDisplay
          alertText={registration?.serviceDeadline?.alertText}
          bgColor={registration?.serviceDeadline?.alertType}
        />
      ),
      children: registration?.expirationDate ? (
        <InProgressServiceCard
          serviceType="registration"
          primaryText={registration?.dueText || ''}
          secondaryText={registration?.serviceDeadline?.timeDiff?.diff || ''}
          progress={registration?.serviceDeadline?.percent || 0}
          handleSubmit={(expirationDate) => {
            const expirationDateString = expirationDate.toString();
            handleCreateRegistration({
              expirationDate: expirationDateString,
              source: 'user',
            });
          }}
        />
      ) : (
        <InitialServiceCard
          primaryText="Get notified of your next registration expiration"
          label="Expiration date"
          inputType="date"
          handleSubmitForm={(expirationDate) => {
            const expirationDateString = expirationDate.toString();
            handleCreateRegistration({
              expirationDate: expirationDateString,
              source: 'user',
            });
          }}
        />
      ),
    },
    {
      icon: imgMyGarage?.imgMyGarageOilChange,
      primaryText: 'Next oil change',
      alertTag: (
        <AlertTextDisplay
          alertText={oilChange?.alertText}
          bgColor={oilChange?.alertType}
        />
      ),
      serviceButton: <Button>Service</Button>,
      children: oilChange ? (
        <InProgressServiceCard
          serviceType="oil change"
          primaryText={oilChange.serviceDueMileageParsed}
          secondaryText={oilChange.milesRemainingText}
          progress={oilChange.percent}
          serviceButton={
            oilChange?.alertText && (
              <ScheduleServiceButton
                type="oil"
                vin={vehicle?.vin}
                zipCode={oramData?.location?.zip}
              />
            )
          }
          handleSubmit={(lastChangeMileage) => {
            handleServiceItemComplete(oilChange.id, lastChangeMileage);
          }}
        />
      ) : (
        <InitialServiceCard
          primaryText="Get notified of next oil change"
          label="Mileage of last oil change"
          handleSubmitForm={(lastChange) =>
            handleCreateServiceItem({
              serviceType: 'oil change',
              suggestedChange: 5000,
              lastChange,
              source: 'user',
            })
          }
        />
      ),
    },
    {
      icon: imgMyGarage?.imgMyGarageTireChange,
      primaryText: 'Next tire change',
      alertTag: (
        <AlertTextDisplay
          alertText={tireChange?.alertText}
          bgColor={tireChange?.alertType}
        />
      ),
      children: tireChange ? (
        <InProgressServiceCard
          serviceType="tire change"
          primaryText={tireChange.serviceDueMileageParsed}
          secondaryText={tireChange.milesRemainingText}
          progress={tireChange.percent}
          serviceButton={
            tireChange?.alertText && (
              <ScheduleServiceButton
                type="tire"
                vin={vehicle?.vin}
                zipCode={oramData?.location?.zip}
              />
            )
          }
          handleSubmit={(lastChangeMileage) => {
            handleServiceItemComplete(tireChange.id, lastChangeMileage);
          }}
        />
      ) : (
        <InitialServiceCard
          primaryText="Get notified of next tire change"
          label="Mileage of last tire change"
          handleSubmitForm={(lastChange) =>
            handleCreateServiceItem({
              serviceType: 'tire change',
              suggestedChange: 45000,
              lastChange,
              source: 'user',
            })
          }
        />
      ),
    },
    {
      icon: imgMyGarage?.imgMyGarageBrake,
      primaryText: 'Next brake change',
      alertTag: (
        <AlertTextDisplay
          alertText={brakeChange?.alertText}
          bgColor={brakeChange?.alertType}
        />
      ),
      children: brakeChange ? (
        <InProgressServiceCard
          serviceType="brake change"
          primaryText={brakeChange.serviceDueMileageParsed}
          secondaryText={brakeChange.milesRemainingText}
          progress={brakeChange.percent}
          serviceButton={
            brakeChange?.alertText && (
              <ScheduleServiceButton
                type="brake"
                vin={vehicle?.vin}
                zipCode={oramData?.location?.zip}
              />
            )
          }
          handleSubmit={(lastChangeMileage) => {
            handleServiceItemComplete(brakeChange.id, lastChangeMileage);
          }}
        />
      ) : (
        <InitialServiceCard
          primaryText="Get notified of next brake change"
          label="Mileage of last brake change"
          handleSubmitForm={(lastChange) =>
            handleCreateServiceItem({
              serviceType: 'brake change',
              suggestedChange: 40000,
              lastChange,
              source: 'user',
            })
          }
        />
      ),
    },
  ];

  return (
    <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
      {serviceCards.map((service) => (
        <Grid key={service.primaryText} item xs={12} md={6}>
          <MyGarageServiceCard {...service} />
        </Grid>
      ))}
    </Grid>
  );
};
